import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  LinearProgress,
  Stack,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '../auth';
import { baseBackendUrl } from '../service/baseService';

interface EmailCampaignSummary {
  id: number;
  subject: string;
  templateName: string;
  mailingListName: string | null;
  sentBy: string | null;
  createdAt: string;
  recipientCount: number;
  sentCount: number;
  failedCount: number;
  openedCount: number;
  openRate: number;
}

const StatTile: React.FC<{ label: string; value: string; accent?: string }> = ({ label, value, accent }) => (
  <Paper variant="outlined" sx={{ p: 2, flex: '1 1 160px', minWidth: 160 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="h4" sx={{ mt: 0.5, color: accent }}>{value}</Typography>
  </Paper>
);

const EmailAnalytics: React.FC = () => {
  const { IsAdmin, cookies } = useContext(useAuth);
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<EmailCampaignSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchCampaigns = async () => {
      if (!cookies?.['UserAuth']?.token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${baseBackendUrl}/api/email/analytics/campaigns`, {
          headers: { 'Authorization': `Bearer ${cookies['UserAuth'].token}` },
        });
        if (!response.ok) throw new Error('Failed to fetch email campaigns');
        const data: EmailCampaignSummary[] = await response.json();
        setCampaigns(data);
      } catch (err) {
        setError('Failed to load email analytics');
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, [cookies]);

  if (!IsAdmin) {
    return (
      <Box sx={{ p: 3, pt: 10, textAlign: 'center' }}>
        <Typography variant="h5" color="error">Access Denied</Typography>
        <Typography>You do not have permission to access this page.</Typography>
      </Box>
    );
  }

  const totalSent = campaigns.reduce((sum, c) => sum + c.sentCount, 0);
  const totalOpened = campaigns.reduce((sum, c) => sum + c.openedCount, 0);
  const totalFailed = campaigns.reduce((sum, c) => sum + c.failedCount, 0);
  const overallOpenRate = totalSent === 0 ? 0 : Math.round((totalOpened / totalSent) * 1000) / 10;

  return (
    <Box sx={{ p: 3, pt: 10, maxWidth: 1200, margin: '0 auto' }}>
      <Typography variant="h4" gutterBottom>Email Analytics</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Every email blast sent from the Communication Center, with delivery and open tracking.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 4, flexWrap: 'wrap', rowGap: 2 }}>
            <StatTile label="Campaigns Sent" value={campaigns.length.toString()} />
            <StatTile label="Emails Sent" value={totalSent.toLocaleString()} />
            <StatTile label="Overall Open Rate" value={`${overallOpenRate}%`} />
            <StatTile label="Send Errors" value={totalFailed.toString()} accent={totalFailed > 0 ? '#d32f2f' : undefined} />
          </Stack>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Subject</TableCell>
                  <TableCell>Mailing List</TableCell>
                  <TableCell>Sent By</TableCell>
                  <TableCell>Date Sent</TableCell>
                  <TableCell align="right">Recipients</TableCell>
                  <TableCell align="right">Sent</TableCell>
                  <TableCell align="right">Errors</TableCell>
                  <TableCell sx={{ minWidth: 160 }}>Open Rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {campaigns.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        No emails have been sent yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {campaigns.map((c) => (
                  <TableRow
                    key={c.id}
                    hover
                    onClick={() => navigate(`/admin/email-analytics/${c.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{c.subject}</TableCell>
                    <TableCell>{c.mailingListName || '—'}</TableCell>
                    <TableCell>{c.sentBy || '—'}</TableCell>
                    <TableCell>{format(new Date(c.createdAt), 'MMM d, yyyy h:mm a')}</TableCell>
                    <TableCell align="right">{c.recipientCount}</TableCell>
                    <TableCell align="right">{c.sentCount}</TableCell>
                    <TableCell align="right" sx={{ color: c.failedCount > 0 ? '#d32f2f' : undefined, fontWeight: c.failedCount > 0 ? 600 : 400 }}>
                      {c.failedCount}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ flex: 1 }}>
                          <LinearProgress variant="determinate" value={c.openRate} sx={{ height: 8, borderRadius: 4 }} />
                        </Box>
                        <Typography variant="body2" sx={{ minWidth: 70, textAlign: 'right' }}>
                          {c.openRate}% ({c.openedCount}/{c.sentCount})
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default EmailAnalytics;
